import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePlaidLink } from "react-plaid-link";
import { QuilttButton, QuilttProvider } from "@quiltt/react";
import api from "../api";
import TaxStatusBadge from "./plaid/TaxStatusBadge";
import SnapTradeLinkButton from "./snaptrade/SnapTradeLinkButton";

const CreateAccountModal = ({ onClose, onCreate, onRefresh }) => {
  // Flow steps: 'choice' -> 'plaid_connecting' / 'quiltt_connecting' -> 'quiltt_account_picker' / 'snaptrade_connecting' -> 'snaptrade_account_picker' / 'account_details' (manual) -> 'import_csv'
  const [step, setStep] = useState("choice");
  const [accountName, setAccountName] = useState("");
  const [taxStatus, setTaxStatus] = useState("TAXABLE");
  const [accountType, setAccountType] = useState("brokerage");
  const [linkToken, setLinkToken] = useState(null);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdAccountId, setCreatedAccountId] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  // Quiltt state
  const [quilttSessionToken, setQuilttSessionToken] = useState(null);
  const [quilttProfileId, setQuilttProfileId] = useState(null);
  const [quilttConnectionId, setQuilttConnectionId] = useState(null);
  const [quilttAccounts, setQuilttAccounts] = useState([]);
  const [quilttInstitution, setQuilttInstitution] = useState(null);
  const [selectedQuilttAccounts, setSelectedQuilttAccounts] = useState({});
  // SnapTrade state
  const [snaptradeAccounts, setSnaptradeAccounts] = useState([]);
  const [selectedSnapTradeAccounts, setSelectedSnapTradeAccounts] = useState(
    {},
  );
  const navigate = useNavigate();

  // Quiltt Connector ID from environment
  const quilttConnectorId = process.env.REACT_APP_QUILTT_CONNECTOR_ID;
  const plaidEnabled =
    (process.env.REACT_APP_PLAID_ENABLED || "true").toLowerCase() === "true";

  // Wrap onClose to log when it's called
  const handleClose = useCallback(() => {
    console.log("Modal closing, current step:", step);
    onClose();
  }, [onClose, step]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && !isSubmitting && !importing) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [handleClose, isSubmitting, importing]);

  // Debug step changes
  useEffect(() => {
    console.log("Step changed to:", step);
  }, [step]);

  // Fetch Plaid link token
  const fetchLinkToken = async () => {
    try {
      setError(null);
      const response = await api.post("/plaid/create-link-token");
      setLinkToken(response.data.link_token);
      return response.data.link_token;
    } catch (err) {
      console.error("Error creating link token:", err);
      setError(err.response?.data?.detail || "Failed to initialize Plaid");
      return null;
    }
  };

  // Handle Plaid Link success - auto-create all accounts
  const handlePlaidSuccess = useCallback(
    async (publicToken, metadata) => {
      console.log("Plaid connected - Full metadata:", metadata);
      setStep("plaid_connecting");
      setIsSubmitting(true);
      setError(null);

      try {
        // Get all accounts user selected
        const accounts = metadata.accounts || [metadata.account];
        console.log(
          `User selected ${accounts.length} account(s):`,
          accounts.map((a) => ({
            name: a.name,
            type: a.type,
            subtype: a.subtype,
            id: a.id,
          })),
        );

        let lastAccountId = null;

        // Step 1: Create all accounts and connect to Plaid
        // onCreate -> App.js createAccount() -> POST /accounts (creates basic account)
        // Then POST /plaid/exchange-public-token (links to Plaid, updates tax_status/account_type)
        const createdAccounts = [];
        for (const account of accounts) {
          console.log(
            `Creating account: ${account.name} (${account.type}/${account.subtype})`,
          );

          // Create account first (just the name for now)
          // Backend will update account_type/tax_status when we link to Plaid below
          const newAccount = await onCreate({
            name: account.name,
          });

          // Connect to Plaid - this updates the account with Plaid details and returns full Account object
          await api.post("/plaid/exchange-public-token", {
            public_token: publicToken,
            plaid_account_id: account.id,
            account_id: newAccount.id,
          });

          lastAccountId = newAccount.id;
          createdAccounts.push({ id: newAccount.id, name: account.name });
          console.log(`✓ Created and connected: ${account.name}`);
        }

        // Step 2: Trigger sync for all accounts in parallel (background process)
        console.log(
          `Triggering background sync for ${createdAccounts.length} account(s)...`,
        );

        // Start syncs in background (don't await - they continue after modal closes)
        createdAccounts.forEach((acc) =>
          api
            .post(`/accounts/${acc.id}/sync-plaid`)
            .then(() => console.log(`✓ Synced: ${acc.name}`))
            .catch((err) =>
              console.error(`✗ Sync failed for ${acc.name}:`, err),
            ),
        );

        // Wait briefly for backend to set sync_in_progress flag
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Refresh to show sync status (the NewDashboardLayout will continue polling)
        if (onRefresh) {
          onRefresh();
        }

        // Success - close modal and navigate
        handleClose();
        if (lastAccountId) {
          navigate(`/account/${lastAccountId}`);
        }
      } catch (error) {
        console.error("Error creating accounts:", error);
        setError(
          error.response?.data?.detail ||
            error.message ||
            "Failed to create accounts",
        );
        setStep("choice");
      }
    },
    [onCreate, onClose, onRefresh, navigate],
  );

  // Handle Plaid Link exit/error
  const handlePlaidExit = useCallback((err) => {
    if (err) {
      console.error("Plaid error:", err);
      setError(err.message || "Plaid connection was cancelled");
    }
    setStep("choice"); // Go back to choice screen
  }, []);

  // Fetch Quiltt session token
  const fetchQuilttSession = async () => {
    try {
      setError(null);
      const response = await api.post("/quiltt/create-session");
      setQuilttSessionToken(response.data.session_token);
      setQuilttProfileId(response.data.profile_id);
      return response.data;
    } catch (err) {
      console.error("Error creating Quiltt session:", err);
      setError(err.response?.data?.detail || "Failed to initialize Quiltt");
      return null;
    }
  };

  // Handle "Connect with Quiltt" button
  const handleConnectQuiltt = async () => {
    setStep("quiltt_connecting");
    const session = await fetchQuilttSession();
    if (!session) {
      setStep("choice"); // Go back on error
    }
    // Session is now set, Quiltt Connector will render
  };

  // Handle successful Quiltt connection - fetch accounts for picker
  const handleQuilttExitSuccess = useCallback(
    async (metadata) => {
      console.log("Quiltt connected - metadata:", metadata);
      setIsSubmitting(true);
      setError(null);

      try {
        // Store connection ID for later
        setQuilttConnectionId(metadata.connectionId);

        // Fetch available accounts from the connection
        const response = await api.post("/quiltt/get-connection-accounts", {
          connection_id: metadata.connectionId,
          profile_id: quilttProfileId,
        });

        const { accounts, institution } = response.data;
        console.log(`Found ${accounts.length} accounts:`, accounts);

        setQuilttAccounts(accounts);
        setQuilttInstitution(institution);

        // Pre-select all accounts by default
        const initialSelection = {};
        accounts.forEach((acc) => {
          initialSelection[acc.id] = true;
        });
        setSelectedQuilttAccounts(initialSelection);

        // Move to account picker step
        setStep("quiltt_account_picker");
      } catch (error) {
        console.error("Error fetching Quiltt accounts:", error);
        setError(
          error.response?.data?.detail ||
            error.message ||
            "Failed to fetch accounts",
        );
        setStep("choice");
      } finally {
        setIsSubmitting(false);
      }
    },
    [quilttProfileId],
  );

  // Handle importing selected Quiltt accounts
  const handleImportQuilttAccounts = async () => {
    const selectedAccounts = quilttAccounts.filter(
      (acc) => selectedQuilttAccounts[acc.id],
    );

    if (selectedAccounts.length === 0) {
      setError("Please select at least one account");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Import selected accounts
      const response = await api.post("/quiltt/import-accounts", {
        connection_id: quilttConnectionId,
        profile_id: quilttProfileId,
        selected_accounts: selectedAccounts,
      });

      console.log("Import result:", response.data);
      const { accounts: createdAccounts } = response.data;

      if (createdAccounts.length === 0) {
        setError("No new accounts were imported (they may already exist)");
        setIsSubmitting(false);
        return;
      }

      // Trigger sync for all created accounts in background
      createdAccounts.forEach((acc) => {
        api
          .post(`/accounts/${acc.id}/sync-quiltt`)
          .then(() => console.log(`✓ Synced: ${acc.name}`))
          .catch((err) => console.error(`✗ Sync failed for ${acc.name}:`, err));
      });

      // Wait briefly for backend to set sync_in_progress flag
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Refresh to show sync status
      if (onRefresh) {
        onRefresh();
      }

      // Success - close modal and navigate to first account
      handleClose();
      if (createdAccounts.length > 0) {
        navigate(`/account/${createdAccounts[0].id}`);
      }
    } catch (error) {
      console.error("Error importing Quiltt accounts:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to import accounts",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle account selection
  const toggleAccountSelection = (accountId) => {
    setSelectedQuilttAccounts((prev) => ({
      ...prev,
      [accountId]: !prev[accountId],
    }));
  };

  // Select/deselect all accounts
  const toggleSelectAll = () => {
    const allSelected = quilttAccounts.every(
      (acc) => selectedQuilttAccounts[acc.id],
    );
    const newSelection = {};
    quilttAccounts.forEach((acc) => {
      newSelection[acc.id] = !allSelected;
    });
    setSelectedQuilttAccounts(newSelection);
  };

  // Configure Plaid Link
  const { open: openPlaid, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: handlePlaidSuccess,
    onExit: handlePlaidExit,
  });

  // Handle "Connect with Plaid" button
  const handleConnectPlaid = async () => {
    if (!plaidEnabled) {
      setError("Plaid integration is disabled");
      return;
    }

    setStep("plaid_connecting");
    const token = await fetchLinkToken();
    if (!token) {
      setStep("choice"); // Go back on error
    }
    // Token is now set, usePlaidLink will initialize
    // We'll open it when ready via useEffect below
  };

  // Auto-open Plaid Link when ready
  React.useEffect(() => {
    if (step === "plaid_connecting" && plaidReady && linkToken) {
      openPlaid();
    }
  }, [step, plaidReady, linkToken, openPlaid]);

  // Handle "Manual Account" button
  const handleManualAccount = () => {
    setStep("account_details");
  };

  const handleConnectSnapTrade = () => {
    setError(null);
    setStep("snaptrade_connecting");
  };

  // Toggle a single SnapTrade account selection
  const toggleSnapTradeAccountSelection = (accountId) => {
    setSelectedSnapTradeAccounts((prev) => ({
      ...prev,
      [accountId]: !prev[accountId],
    }));
  };

  // Select/deselect all SnapTrade accounts (never touches already-connected ones)
  const toggleSelectAllSnapTrade = () => {
    const selectableAccounts = snaptradeAccounts.filter(
      (acc) => !acc.already_connected,
    );
    const allSelected = selectableAccounts.every(
      (acc) => selectedSnapTradeAccounts[acc.id],
    );
    const newSelection = { ...selectedSnapTradeAccounts };
    selectableAccounts.forEach((acc) => {
      newSelection[acc.id] = !allSelected;
    });
    setSelectedSnapTradeAccounts(newSelection);
  };

  // Import selected SnapTrade accounts
  const handleImportSnapTradeAccounts = async () => {
    const selectedAccounts = snaptradeAccounts.filter(
      (acc) => selectedSnapTradeAccounts[acc.id],
    );

    if (selectedAccounts.length === 0) {
      setError("Please select at least one account");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const createdAccounts = [];
      for (const stAccount of selectedAccounts) {
        const newAccount = await onCreate({
          name: stAccount.name || "SnapTrade Account",
        });
        await api.post("/snaptrade/link-account", {
          snaptrade_account_id: stAccount.id,
          account_id: newAccount.id,
        });
        createdAccounts.push({ id: newAccount.id, name: stAccount.name });
      }

      // link-account now queues the background sync — no separate call needed
      await new Promise((resolve) => setTimeout(resolve, 500));

      if (onRefresh) onRefresh();
      handleClose();
      if (createdAccounts.length > 0) {
        navigate(`/account/${createdAccounts[0].id}`);
      }
    } catch (error) {
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to import accounts",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle manual account creation
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!accountName.trim()) {
      setError("Please enter an account name");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // Create manual account
      const newAccount = await onCreate({
        name: accountName.trim(),
        tax_status: taxStatus,
        account_type: accountType,
      });

      console.log("Account created successfully:", newAccount);

      // Store account ID and move to CSV import step
      setCreatedAccountId(newAccount.id);
      setIsSubmitting(false); // Reset before moving to next step

      console.log("About to set step to import_csv, current step:", step);

      // Use setTimeout to ensure state update happens in next tick
      setTimeout(() => {
        setStep("import_csv");
        console.log("Moving to import_csv step - setStep called");
      }, 0);
    } catch (error) {
      console.error("Error creating account:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to create account",
      );
      setIsSubmitting(false);
    }
  };

  // CSV Import handlers
  const handleFileSelect = async (file) => {
    if (
      !file ||
      !(file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv"))
    ) {
      setError("Please select a valid CSV file");
      return;
    }

    // Peek at the header to detect positions vs transaction CSV
    const csvType = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const firstLine = (e.target.result || "").split("\n")[0];
        if (
          firstLine.includes("Account Number") &&
          firstLine.includes("Investment Type")
        ) {
          resolve("positions");
        } else {
          resolve("transactions");
        }
      };
      reader.onerror = () => resolve("transactions");
      reader.readAsText(file.slice(0, 500));
    });

    if (csvType === "positions") {
      setError(
        "This looks like a Portfolio Positions file (it contains multiple accounts). " +
          "Close this dialog and drop the file on the main page to import it.",
      );
      return;
    }

    setCsvFile(file);
    setError(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent GlobalDropZone from showing overlay
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent GlobalDropZone from intercepting
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleImportCSV = async () => {
    if (!csvFile || !createdAccountId) return;

    try {
      setImporting(true);
      setError(null);

      const formData = new FormData();
      formData.append("file", csvFile);

      await api.post(`/accounts/${createdAccountId}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Success - refresh and navigate
      if (onRefresh) onRefresh();
      handleClose();
      navigate(`/account/${createdAccountId}`);
    } catch (err) {
      console.error("CSV import failed:", err);
      setError(
        err.response?.data?.detail || "Import failed. Please try again.",
      );
    } finally {
      setImporting(false);
    }
  };

  const handleSkipImport = () => {
    // Skip import and navigate to account
    if (onRefresh) onRefresh();
    handleClose();
    if (createdAccountId) {
      navigate(`/account/${createdAccountId}`);
    }
  };

  // Render different UI based on current step
  const renderContent = () => {
    console.log("=== RENDER CONTENT ===");
    console.log("Rendering step:", step, "createdAccountId:", createdAccountId);
    console.log("===================");

    // Step 1: Choose connection method
    if (step === "choice") {
      return (
        <>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Add Account
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Choose how you'd like to connect your brokerage account
          </p>

          <div className="space-y-3">
            {/* Plaid Option */}
            {plaidEnabled && (
              <button
                onClick={handleConnectPlaid}
                className="w-full px-6 py-5 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all text-left group"
              >
                <div className="flex items-start gap-4">
                  <div className="text-4xl">🏦</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Connect with Plaid
                      </h3>
                      <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full font-medium">
                        Recommended
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Securely connect your brokerage and automatically sync
                      transactions
                    </p>
                    <ul className="mt-2 space-y-1 text-xs text-gray-500 dark:text-gray-500">
                      <li className="flex items-center gap-1.5">
                        <svg
                          className="w-3.5 h-3.5 text-green-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span>Automatic daily sync</span>
                      </li>
                      <li className="flex items-center gap-1.5">
                        <svg
                          className="w-3.5 h-3.5 text-green-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span>No manual CSV uploads</span>
                      </li>
                      <li className="flex items-center gap-1.5">
                        <svg
                          className="w-3.5 h-3.5 text-green-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span>Bank-level security</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </button>
            )}

            {/* Quiltt Option - for Fidelity and other institutions */}
            {quilttConnectorId && (
              <button
                onClick={handleConnectQuiltt}
                className="w-full px-6 py-5 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-purple-500 dark:hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-all text-left group"
              >
                <div className="flex items-start gap-4">
                  <div className="text-4xl">🔗</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Connect with Quiltt
                      </h3>
                      <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full font-medium">
                        Fidelity
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Connect Fidelity and other institutions not supported by
                      Plaid
                    </p>
                    <ul className="mt-2 space-y-1 text-xs text-gray-500 dark:text-gray-500">
                      <li className="flex items-center gap-1.5">
                        <svg
                          className="w-3.5 h-3.5 text-green-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span>Supports Fidelity, Vanguard, and more</span>
                      </li>
                      <li className="flex items-center gap-1.5">
                        <svg
                          className="w-3.5 h-3.5 text-green-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span>Multiple data providers for better coverage</span>
                      </li>
                      <li className="flex items-center gap-1.5">
                        <svg
                          className="w-3.5 h-3.5 text-green-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span>Automatic daily sync</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </button>
            )}

            {/* SnapTrade Option */}
            <button
              onClick={handleConnectSnapTrade}
              disabled={isSubmitting}
              className="w-full px-6 py-5 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-green-500 dark:hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/10 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-start gap-4">
                <div className="text-4xl">📈</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Connect with SnapTrade
                    </h3>
                    <span className="text-center text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full font-medium">
                      20+ Brokers
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Connect retail brokerages including Interactive Brokers,
                    Questrade, Wealthsimple, and TD
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-gray-500 dark:text-gray-500">
                    <li className="flex items-center gap-1.5">
                      <svg
                        className="w-3.5 h-3.5 text-green-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>
                        Supports IBKR, Questrade, Wealthsimple, TD & more
                      </span>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <svg
                        className="w-3.5 h-3.5 text-green-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>Holdings and transaction history sync</span>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <svg
                        className="w-3.5 h-3.5 text-green-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>Secure hosted connection portal</span>
                    </li>
                  </ul>
                </div>
              </div>
            </button>

            {/* Manual Option */}
            <button
              onClick={handleManualAccount}
              className="w-full px-6 py-5 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all text-left"
            >
              <div className="flex items-start gap-4">
                <div className="text-4xl">📄</div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    Manual CSV Import
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Create account and upload transaction history from CSV files
                  </p>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                    Best for brokers not supported by Plaid/Quiltt or if you
                    prefer manual control
                  </p>
                </div>
              </div>
            </button>
          </div>

          <button
            onClick={handleClose}
            className="mt-6 w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            Cancel
          </button>
        </>
      );
    }

    // Step 2: Connecting to Plaid (loading state + auto-creating accounts)
    console.log("Current step:", step);
    if (step === "plaid_connecting") {
      return (
        <>
          <div className="text-center py-12">
            <svg
              className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {isSubmitting ? "Creating accounts..." : "Opening Plaid..."}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isSubmitting
                ? "Importing your accounts and syncing transactions"
                : "The Plaid connection window should open shortly"}
            </p>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              <button
                onClick={() => setStep("choice")}
                className="mt-2 text-sm text-red-700 dark:text-red-300 underline"
              >
                Go back
              </button>
            </div>
          )}
        </>
      );
    }

    // Step 2b: Connecting to Quiltt
    if (step === "quiltt_connecting") {
      // If still fetching session token, show loading
      if (!quilttSessionToken) {
        return (
          <>
            <div className="text-center py-12">
              <svg
                className="animate-spin h-12 w-12 text-purple-600 mx-auto mb-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Initializing Quiltt...
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Please wait while we set up your connection
              </p>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
                <button
                  onClick={() => setStep("choice")}
                  className="mt-2 text-sm text-red-700 dark:text-red-300 underline"
                >
                  Go back
                </button>
              </div>
            )}
          </>
        );
      }

      // Show Quiltt Connector
      return (
        <>
          <div className="text-center py-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {isSubmitting ? "Creating account..." : "Connect Your Account"}
            </h3>

            {isSubmitting ? (
              <div className="flex flex-col items-center">
                <svg
                  className="animate-spin h-12 w-12 text-purple-600 mb-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Importing your account and syncing transactions...
                </p>
              </div>
            ) : (
              <QuilttProvider token={quilttSessionToken}>
                <QuilttButton
                  connectorId={quilttConnectorId}
                  onExitSuccess={handleQuilttExitSuccess}
                  onLoad={() => console.log("Quiltt Connector loaded")}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  Connect with Quiltt
                </QuilttButton>
              </QuilttProvider>
            )}
          </div>

          {!isSubmitting && (
            <button
              onClick={() => {
                setQuilttSessionToken(null);
                setQuilttProfileId(null);
                setStep("choice");
              }}
              className="mt-4 w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              Back to options
            </button>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </>
      );
    }

    // Step 2c: Quiltt Account Picker
    if (step === "quiltt_account_picker") {
      const selectedCount = Object.values(selectedQuilttAccounts).filter(
        Boolean,
      ).length;
      const allSelected = quilttAccounts.every(
        (acc) => selectedQuilttAccounts[acc.id],
      );

      return (
        <>
          <div className="flex items-center gap-3 mb-2">
            {quilttInstitution?.logo && (
              <img
                src={quilttInstitution.logo}
                alt={quilttInstitution.name}
                className="w-10 h-10 rounded-lg"
              />
            )}
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Select Accounts
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {quilttInstitution?.name || "Connected Institution"}
              </p>
            </div>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Choose which accounts to import. Each will be tracked separately.
          </p>

          {/* Select All */}
          <button
            onClick={toggleSelectAll}
            className="w-full mb-3 px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <span className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="font-medium">
                {allSelected ? "Deselect All" : "Select All"}
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                ({quilttAccounts.length} accounts)
              </span>
            </span>
          </button>

          {/* Account List */}
          <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
            {quilttAccounts.map((acc) => (
              <label
                key={acc.id}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedQuilttAccounts[acc.id]
                    ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20"
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedQuilttAccounts[acc.id] || false}
                  onChange={() => toggleAccountSelection(acc.id)}
                  className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">
                    {acc.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {acc.type} {acc.mask && `•••• ${acc.mask}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900 dark:text-white">
                    $
                    {(acc.balance || 0).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </label>
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setQuilttSessionToken(null);
                setQuilttProfileId(null);
                setQuilttConnectionId(null);
                setQuilttAccounts([]);
                setStep("choice");
              }}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleImportQuilttAccounts}
              disabled={isSubmitting || selectedCount === 0}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Importing...
                </span>
              ) : (
                `Import ${selectedCount} Account${selectedCount !== 1 ? "s" : ""}`
              )}
            </button>
          </div>
        </>
      );
    }

    // SnapTrade: Open hosted portal to link brokerage
    if (step === "snaptrade_connecting") {
      return (
        <>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Connect Your Brokerage
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Click the button below to open the SnapTrade portal and authorize
            access to your brokerage account
          </p>

          <div className="flex flex-col items-center gap-4 py-4">
            {isSubmitting ? (
              <div className="flex flex-col items-center gap-3">
                <svg
                  className="animate-spin h-10 w-10 text-green-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Fetching your accounts...
                </p>
              </div>
            ) : (
              <SnapTradeLinkButton
                onSuccess={async ({ authorizationId }) => {
                  console.log(
                    "SnapTrade portal success, authorizationId:",
                    authorizationId,
                  );
                  setIsSubmitting(true);
                  setError(null);
                  try {
                    const response = await api.post("/snaptrade/get-accounts", {
                      authorization_id: authorizationId,
                    });
                    const accounts = response.data.accounts || [];
                    setSnaptradeAccounts(accounts);
                    const initialSelection = {};
                    accounts.forEach((acc) => {
                      // Pre-select only new accounts — already-connected ones are disabled
                      initialSelection[acc.id] = !acc.already_connected;
                    });
                    setSelectedSnapTradeAccounts(initialSelection);
                    setStep("snaptrade_account_picker");
                  } catch (err) {
                    setError(
                      err.response?.data?.detail ||
                        err.message ||
                        "Failed to fetch accounts",
                    );
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                onError={(err) => setError(err?.message || "Connection failed")}
                buttonText="Open SnapTrade Portal"
                className="w-full px-6 py-3 text-base font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 transition-colors"
              />
            )}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {!isSubmitting && (
            <button
              onClick={() => setStep("choice")}
              className="mt-2 w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              Back to options
            </button>
          )}
        </>
      );
    }

    // SnapTrade: Account picker
    if (step === "snaptrade_account_picker") {
      const selectableAccounts = snaptradeAccounts.filter(
        (acc) => !acc.already_connected,
      );
      const selectedCount = selectableAccounts.filter(
        (acc) => selectedSnapTradeAccounts[acc.id],
      ).length;
      const allSelected =
        selectableAccounts.length > 0 &&
        selectableAccounts.every((acc) => selectedSnapTradeAccounts[acc.id]);
      const nothingNew = selectableAccounts.length === 0;

      return (
        <>
          <div className="flex items-center gap-3 mb-2">
            <div className="text-3xl">📈</div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Select Accounts
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Choose which accounts to import
              </p>
            </div>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Each selected account will be tracked separately.
          </p>

          {/* Select All — only shown when there are selectable accounts */}
          {!nothingNew && (
            <button
              onClick={toggleSelectAllSnapTrade}
              className="w-full mb-3 px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <span className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAllSnapTrade}
                  className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="font-medium">
                  {allSelected ? "Deselect All" : "Select All"}
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  ({selectableAccounts.length} account
                  {selectableAccounts.length !== 1 ? "s" : ""})
                </span>
              </span>
            </button>
          )}

          {/* Account List */}
          <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
            {snaptradeAccounts.map((acc) =>
              acc.already_connected ? (
                <div
                  key={acc.id}
                  className="flex items-center gap-3 p-3 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
                >
                  <div className="w-4 h-4 shrink-0 flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900/40">
                    <svg
                      className="w-2.5 h-2.5 text-green-600 dark:text-green-400"
                      fill="currentColor"
                      viewBox="0 0 12 12"
                    >
                      <path
                        d="M10 3L5 8.5 2 5.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-500 dark:text-gray-400 truncate">
                      {acc.name || "Unnamed Account"}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {acc.number ? `•••• ${acc.number.slice(-4)}` : ""}
                      {acc.brokerage_authorization?.brokerage?.name
                        ? ` · ${acc.brokerage_authorization.brokerage.name}`
                        : ""}
                      {" · "}Already in WealthPort
                    </p>
                  </div>
                </div>
              ) : (
                <label
                  key={acc.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedSnapTradeAccounts[acc.id]
                      ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSnapTradeAccounts[acc.id] || false}
                    onChange={() => toggleSnapTradeAccountSelection(acc.id)}
                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">
                      {acc.name || "Unnamed Account"}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {acc.number ? `•••• ${acc.number.slice(-4)}` : ""}
                      {acc.brokerage_authorization?.brokerage?.name
                        ? ` · ${acc.brokerage_authorization.brokerage.name}`
                        : ""}
                    </p>
                  </div>
                </label>
              ),
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setSnaptradeAccounts([]);
                setSelectedSnapTradeAccounts({});
                setStep("choice");
              }}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleImportSnapTradeAccounts}
              disabled={isSubmitting || selectedCount === 0 || nothingNew}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Importing...
                </span>
              ) : (
                `Import ${selectedCount} Account${selectedCount !== 1 ? "s" : ""}`
              )}
            </button>
          </div>
        </>
      );
    }

    // Step 3: Manual account creation form
    if (step === "account_details") {
      return (
        <>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Account Details
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Provide a name and classification for your account
          </p>

          <form onSubmit={handleCreateAccount}>
            {/* Account Name */}
            <div className="mb-4">
              <label
                htmlFor="account-name"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Account Name
              </label>
              <input
                id="account-name"
                type="text"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder="e.g., Fidelity Brokerage"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                autoFocus
                disabled={isSubmitting}
              />
            </div>

            {/* Tax Status */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tax Status
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: "TAXABLE", label: "Taxable" },
                  { value: "TAX_DEFERRED", label: "Tax-Deferred" },
                  { value: "TAX_FREE", label: "Tax-Free" },
                  { value: "HSA", label: "HSA" },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setTaxStatus(option.value)}
                    disabled={isSubmitting}
                    className={`px-3 py-2 rounded-lg border-2 transition-colors ${
                      taxStatus === option.value
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        : "border-gray-300 dark:border-gray-600 hover:border-gray-400"
                    } disabled:opacity-50`}
                  >
                    <TaxStatusBadge taxStatus={option.value} size="sm" />
                  </button>
                ))}
              </div>
            </div>

            {/* Account Type */}
            <div className="mb-6">
              <label
                htmlFor="account-type"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Account Type
              </label>
              <select
                id="account-type"
                value={accountType}
                onChange={(e) => setAccountType(e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="brokerage">Brokerage</option>
                <option value="ira">IRA</option>
                <option value="401k">401(k)</option>
                <option value="roth_ira">Roth IRA</option>
                <option value="roth_401k">Roth 401(k)</option>
              </select>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep("choice")}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Creating...
                  </span>
                ) : (
                  "Create Account"
                )}
              </button>
            </div>
          </form>
        </>
      );
    }

    // Step 4: CSV Import
    if (step === "import_csv") {
      console.log("!!! RENDERING IMPORT_CSV STEP !!!");
      return (
        <>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Import Transactions
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Upload a CSV file with your transaction history, or skip to add
            holdings manually later
          </p>

          {/* File Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById("csv-file-input").click()}
            className={`mb-6 border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              isDragging
                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                : csvFile
                  ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                  : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
            }`}
          >
            {csvFile ? (
              <>
                <div className="text-4xl mb-3">✓</div>
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  {csvFile.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Click to choose a different file
                </p>
              </>
            ) : (
              <>
                <div className="text-4xl mb-3">📄</div>
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  Drop CSV file here or click to browse
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Supported format: CSV transaction history from your broker
                </p>
              </>
            )}
          </div>

          <input
            id="csv-file-input"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              if (e.target.files?.[0]) {
                handleFileSelect(e.target.files[0]);
              }
            }}
            className="hidden"
          />

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Info */}
          <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              💡 <strong>Tip:</strong> First import builds your complete account
              from transaction history. You can always import more later.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleSkipImport}
              disabled={importing}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Skip for Now
            </button>
            <button
              type="button"
              onClick={handleImportCSV}
              disabled={!csvFile || importing}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {importing ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Importing...
                </span>
              ) : (
                "Import & Continue"
              )}
            </button>
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[10001]"
      onClick={(e) => {
        if (
          e.target === e.currentTarget &&
          step !== "plaid_connecting" &&
          step !== "quiltt_connecting" &&
          step !== "quiltt_account_picker" &&
          step !== "snaptrade_connecting" &&
          step !== "snaptrade_account_picker" &&
          !isSubmitting &&
          !importing
        ) {
          handleClose();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default CreateAccountModal;
